import { Injectable, inject } from '@angular/core';
import { ToastService } from './toast.service';
import { PassportDetailsStore } from './passport-details.store';

/**
 * Helper service for checking HACCP image load failures and showing toast notifications
 * Used across all persona components
 */
@Injectable({
  providedIn: 'root'
})
export class ImageLoadHelperService {
  private readonly toastService = inject(ToastService);
  private readonly detailsStore = inject(PassportDetailsStore);
  private readonly shownToasts = new Map<string, number>(); // Track which passports showed toast and when

  /**
   * Check if images failed to load and show toast notification (online mode only)
   * @param passportId The passport ID to check
   * @param actualLoadedImagesCount Number of images actually loaded successfully
   */
  checkAndShowImageLoadFailure(passportId: string, actualLoadedImagesCount: number = 0): void {
    // Only show toast when online
    if (!navigator.onLine) {
      return;
    }

    const passportData = this.detailsStore.get(passportId);
    const expectedImageCount = passportData?.imageCount || 0;

    // No expected images, nothing to check
    if (expectedImageCount === 0) {
      return;
    }

    // Check if we already showed a toast for this passport recently (within last 10 seconds)
    const lastShown = this.shownToasts.get(passportId);
    const now = Date.now();
    if (lastShown && (now - lastShown) < 10000) {
      return; // Don't show duplicate toasts within 10 seconds
    }

    // Calculate if there's a failure
    const hasFailure = actualLoadedImagesCount < expectedImageCount;
    
    if (!hasFailure) {
      return; // All images loaded successfully
    }

    // Show generic unavailability message if ANY images failed to load
    const message = expectedImageCount === 1 
      ? 'HACCP image temporarily unavailable.'
      : 'HACCP images temporarily unavailable.';
    
    this.toastService.warning(message, 5000);
    this.shownToasts.set(passportId, now);
    
    console.warn(`⚠️ Image load failure for passport ${passportId}: Expected ${expectedImageCount} images but loaded ${actualLoadedImagesCount}`);
  }

  /**
   * Clear the shown toasts tracking (useful for refresh operations)
   */
  clearShownToasts(): void {
    this.shownToasts.clear();
  }
}

